<template>
  <!--
    Light, dark or the device's setting, as three small icon buttons. The
    single variant (the collapsed sidebar) is one button that steps through
    them, and takes its look from the class it is given.
  -->
  <button
    v-if="single"
    v-tooltip.right="`Theme: ${current.label}. Click for ${themeModeOption(nextThemeMode(mode)).label}`"
    type="button"
    class="kb-theme-one"
    :aria-label="`Theme: ${current.label}. Switch to ${themeModeOption(nextThemeMode(mode)).label}`"
    @click="cycle"
  >
    <i :class="['pi', current.icon]" />
  </button>
  <div
    v-else
    class="kb-theme-switch"
    role="group"
    aria-label="Theme"
  >
    <button
      v-for="option in THEME_MODES"
      :key="option.value"
      v-tooltip.top="option.value === 'system' ? 'System: follows your device' : option.label"
      type="button"
      :aria-label="option.label"
      :aria-pressed="mode === option.value"
      @click="setMode(option.value)"
    >
      <i :class="['pi', option.icon]" />
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useThemeMode } from '~/composables/useThemeMode'
import { nextThemeMode, THEME_MODES, themeModeOption } from '~/utils/themeMode'

defineProps<{ single?: boolean }>()

const { mode, setMode, cycle } = useThemeMode()
const current = computed(() => themeModeOption(mode.value))
</script>

<style>
.kb-theme-switch {
  display: inline-flex;
  gap: 1px;
  padding: 2px;
  border: 1px solid var(--kb-line);
  border-radius: 4px;
  background: var(--kb-bg);
}

.kb-theme-switch > button {
  display: grid;
  place-items: center;
  width: 1.625rem;
  height: 1.375rem;
  border-radius: 2px;
  color: var(--kb-dim);
}

.kb-theme-switch > button .pi {
  font-size: 12px;
}

/* Fingers need more than a mouse */
@media (pointer: coarse) {
  .kb-theme-switch > button {
    width: 2.25rem;
    height: 1.875rem;
  }

  .kb-theme-switch > button .pi {
    font-size: 14px;
  }
}

.kb-theme-switch > button:hover {
  color: var(--kb-fg);
}

.kb-theme-switch > button[aria-pressed="true"] {
  background: var(--kb-raised);
  color: var(--kb-accent);
  box-shadow: 0 0 0 1px var(--kb-line);
}

.kb-theme-switch > button:focus-visible,
.kb-theme-one:focus-visible {
  outline: 2px solid var(--kb-accent);
  outline-offset: 1px;
}
</style>
